/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Edge-Native Template Interpolation Engine
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { PromptVariables, PromptVariableValue, ValidationResult } from './types';

/**
 * Evaluates whether a variable value is considered truthy in template logic.
 */
function isTruthy(value: PromptVariableValue): boolean {
  if (value === null || value === undefined || value === false || value === '' || value === 0) {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
}

/**
 * Formats a variable value for string substitution.
 */
function formatValue(value: PromptVariableValue): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
}

/**
 * TemplateInterpolationEngine: Edge-compatible (V8 Isolate) template parser.
 * Supports dynamic variables, conditionals, defaults, and required field validation
 * without relying on unsafe eval() or Function constructors.
 */
export class TemplateInterpolationEngine {
  /**
   * Extract all variable names used across the template (placeholders, conditionals, defaults).
   */
  public static extractVariables(template: string): string[] {
    if (!template || typeof template !== 'string') {
      return [];
    }

    const variables = new Set<string>();

    // 1. Match {{#if var}} or {{#unless var}}
    const ifRegex = /\{\{#(?:if|unless)\s+([a-zA-Z0-9_.-]+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = ifRegex.exec(template)) !== null) {
      if (match[1]) {
        variables.add(match[1]);
      }
    }

    // 2. Match {{var}} or {{var | default: ...}}
    const varRegex = /\{\{\s*([a-zA-Z0-9_.-]+)(?:\s*\|\s*default:[^}]+)?\s*\}\}/g;
    while ((match = varRegex.exec(template)) !== null) {
      const varName = match[1];
      // Exclude block keywords
      if (varName && !varName.startsWith('#') && !varName.startsWith('/') && varName !== 'else') {
        variables.add(varName);
      }
    }

    return Array.from(variables);
  }

  /**
   * Extract only strictly required variables (bare placeholders without defaults or conditionals).
   */
  public static extractRequiredVariables(template: string): string[] {
    if (!template || typeof template !== 'string') {
      return [];
    }

    const optionalVars = new Set<string>();

    // 1. Conditionals: {{#if var}} or {{#unless var}}
    const ifRegex = /\{\{#(?:if|unless)\s+([a-zA-Z0-9_.-]+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = ifRegex.exec(template)) !== null) {
      if (match[1]) {
        optionalVars.add(match[1]);
      }
    }

    // 2. Variables with default fallback: {{var | default: ...}}
    const defaultRegex = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\|\s*default:[^}]+\}\}/g;
    while ((match = defaultRegex.exec(template)) !== null) {
      if (match[1]) {
        optionalVars.add(match[1]);
      }
    }

    // 3. Collect bare variables not marked optional
    const requiredVars = new Set<string>();
    const simpleVarRegex = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
    while ((match = simpleVarRegex.exec(template)) !== null) {
      const varName = match[1];
      if (
        varName &&
        !varName.startsWith('#') &&
        !varName.startsWith('/') &&
        varName !== 'else' &&
        !optionalVars.has(varName)
      ) {
        requiredVars.add(varName);
      }
    }

    return Array.from(requiredVars);
  }

  /**
   * Validate that all required variables are present and non-empty.
   */
  public static validate(
    template: string,
    variables: PromptVariables,
    requiredFields?: string[]
  ): ValidationResult {
    const missingFields: string[] = [];
    const fieldsToCheck = requiredFields && requiredFields.length > 0
      ? requiredFields
      : this.extractRequiredVariables(template);

    for (const field of fieldsToCheck) {
      const val = variables[field];
      if (val === undefined || val === null || val === '') {
        missingFields.push(field);
      }
    }

    if (missingFields.length > 0) {
      return {
        isValid: false,
        missingFields,
        error: `Missing required template variable(s): ${missingFields.join(', ')}`,
      };
    }

    return {
      isValid: true,
      missingFields: [],
    };
  }

  /**
   * Interpolate dynamic placeholders, conditionals, and default fallbacks.
   */
  public static interpolate(
    template: string,
    variables: PromptVariables,
    options: { strict?: boolean; defaultFallbacks?: Record<string, string> } = {}
  ): string {
    if (!template || typeof template !== 'string') {
      return '';
    }

    const { strict = false, defaultFallbacks = {} } = options;
    let result = template;

    // 1. Process {{#if var}}...{{else}}...{{/if}} and {{#if var}}...{{/if}}
    // Supports nested or sequential conditionals
    const conditionalRegex =
      /\{\{#if\s+([a-zA-Z0-9_.-]+)\s*\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g;

    result = result.replace(conditionalRegex, (_, varName, ifBranch, elseBranch = '') => {
      const val = variables[varName];
      return isTruthy(val) ? ifBranch : elseBranch;
    });

    // 2. Process {{#unless var}}...{{/unless}}
    const unlessRegex =
      /\{\{#unless\s+([a-zA-Z0-9_.-]+)\s*\}\}([\s\S]*?)\{\{\/unless\}\}/g;

    result = result.replace(unlessRegex, (_, varName, branch) => {
      const val = variables[varName];
      return !isTruthy(val) ? branch : '';
    });

    // 3. Process variables with default filter: {{var | default: 'fallback'}} or {{var | default: 0.7}}
    const defaultRegex =
      /\{\{\s*([a-zA-Z0-9_.-]+)\s*\|\s*default:\s*(?:'([^']*)'|"([^"]*)"|([^}\s]+))\s*\}\}/g;

    result = result.replace(defaultRegex, (_, varName, singleQuote, doubleQuote, rawVal) => {
      const fallback = singleQuote ?? doubleQuote ?? rawVal ?? '';
      const val = variables[varName];
      if (val !== undefined && val !== null && val !== '') {
        return formatValue(val);
      }
      return fallback;
    });

    // 4. Process simple variables: {{var}} or {{ var }}
    const simpleVarRegex = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;

    result = result.replace(simpleVarRegex, (_, varName) => {
      const val = variables[varName];
      if (val !== undefined && val !== null) {
        return formatValue(val);
      }
      if (defaultFallbacks[varName] !== undefined) {
        return String(defaultFallbacks[varName]);
      }
      if (strict) {
        throw new Error(`Unresolved template variable in strict mode: '{{${varName}}}'`);
      }
      // Leave intact or return empty? In standard prompt builders, unprovided optional vars default to empty
      return '';
    });

    return result.trim();
  }
}
